import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle, ArrowLeft } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const doorSlug = (searchParams.get("door") || "").toLowerCase().replace(/[^a-z0-9-]/g, "");
  const [door, setDoor] = useState(null); // {slug, company_name, logo_url, primary_color}
  const [loginPath, setLoginPath] = useState(doorSlug ? `/${doorSlug}/login` : "/login");

  useEffect(() => {
    if (!doorSlug) return;
    axios.get(`${BACKEND}/api/public/tenant-portal/${encodeURIComponent(doorSlug)}`)
      .then(({ data }) => setDoor(data))
      .catch(() => setLoginPath("/login"));
  }, [doorSlug]);
  const doorLogo = door?.logo_url ? (door.logo_url.startsWith("http") ? door.logo_url : `${BACKEND}${door.logo_url}`) : "";
  const backLabel = door ? `Back to ${door.company_name} sign-in` : "Back to Login";

  // Forgot password form (no token)
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Reset password form (with token)
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post("/auth/forgot-password", { email });
      setEmailSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await API.post("/auth/reset-password", { token, password });
      if (data?.login_path) setLoginPath(data.login_path);
      setResetDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {door ? (
            <div data-testid="reset-door-brand">
              {doorLogo && <img src={doorLogo} alt={door.company_name} className="h-12 max-w-[220px] object-contain mx-auto mb-2" />}
              <h1 className="text-2xl font-bold font-[Chivo] tracking-tight">{door.company_name}</h1>
              <p className="text-sm text-muted-foreground mt-1">Workspace account</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold font-[Chivo] tracking-tight">Fixlr</h1>
              <p className="text-sm text-muted-foreground mt-1">Property Project Management</p>
            </>
          )}
        </div>

        <div className="border border-border rounded-lg p-6 bg-card shadow-sm">
          {/* No token = Forgot Password form */}
          {!token && !emailSent && (
            <>
              <h2 className="text-lg font-semibold font-[Chivo] mb-1">Forgot Password</h2>
              <p className="text-sm text-muted-foreground mb-6">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="your@email.com" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            </>
          )}

          {/* Email sent confirmation */}
          {!token && emailSent && (
            <div className="text-center py-4" data-testid="reset-email-sent">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={24} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold font-[Chivo] mb-2">Check Your Email</h2>
              <p className="text-sm text-muted-foreground mb-4">If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a password reset link. The link expires in 1 hour.</p>
              <p className="text-xs text-muted-foreground">Didn't receive it? Check your spam folder or <button className="text-primary underline" onClick={() => setEmailSent(false)}>try again</button>.</p>
            </div>
          )}

          {/* Token present = Reset Password form */}
          {token && !resetDone && (
            <>
              <h2 className="text-lg font-semibold font-[Chivo] mb-1">Set New Password</h2>
              <p className="text-sm text-muted-foreground mb-6">Enter your new password below.</p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label>New Password</Label>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} placeholder="Min. 8 characters" data-testid="new-password-input" />
                </div>
                <div>
                  <Label>Confirm Password</Label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required placeholder="Confirm your password" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}

          {/* Reset success */}
          {token && resetDone && (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={24} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold font-[Chivo] mb-2">Password Reset</h2>
              <p className="text-sm text-muted-foreground mb-4">Your password has been successfully updated.</p>
              <a href={loginPath}>
                <Button className="w-full" style={door?.primary_color ? { backgroundColor: door.primary_color } : undefined} data-testid="back-to-login-btn">{door ? `Sign in to ${door.company_name}` : "Back to Login"}</Button>
              </a>
            </div>
          )}

          {/* Back to login link */}
          {!resetDone && (
            <div className="mt-4 text-center">
              <a href={loginPath} className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="back-to-login-link">
                <ArrowLeft size={12} /> {backLabel}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
